import type { FC, Ref } from 'react';
import { useState, useCallback } from 'react';
import type { MenuToggleElement } from '@patternfly/react-core';
import { MenuToggle } from '@patternfly/react-core/dist/dynamic/components/MenuToggle';
import { Select } from '@patternfly/react-core/dist/dynamic/components/Select';
import { SelectList } from '@patternfly/react-core/dist/dynamic/components/Select';
import { SelectOption } from '@patternfly/react-core/dist/dynamic/components/Select';
import { useTranslation } from 'react-i18next';
import type { UtilizationDurationDropdownProps } from '@console/dynamic-plugin-sdk/src/api/internal-types';
import { DurationKeys, DURATION_VALUES } from '../../../constants/duration';
import { useUtilizationDuration } from '../../../hooks/useUtilizationDuration';

export const UtilizationDurationDropdown: FC<UtilizationDurationDropdownProps> = ({
  adjustDuration,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { selectedKey, updateSelectedKey, updateDuration } = useUtilizationDuration(adjustDuration);
  const items = {
    [DurationKeys.OneHour]: t('console-shared~1 hour'),
    [DurationKeys.SixHours]: t('console-shared~6 hours'),
    [DurationKeys.TwentyFourHours]: t('console-shared~24 hours'),
  };

  const onSelect = useCallback(
    (event, newSelected) => {
      updateSelectedKey(newSelected);
      updateDuration(DURATION_VALUES[newSelected]);
      setIsOpen(false);
    },
    [updateDuration, updateSelectedKey],
  );

  return (
    <div data-test-id="duration-select">
      <Select
        toggle={(toggleRef: Ref<MenuToggleElement>) => (
          <MenuToggle
            ref={toggleRef}
            onClick={(open) => setIsOpen(open)}
            isExpanded={isOpen}
            variant="plainText"
          >
            {items[selectedKey]}
          </MenuToggle>
        )}
        onSelect={onSelect}
        selected={selectedKey}
        onOpenChange={(open) => setIsOpen(open)}
        isOpen={isOpen}
      >
        <SelectList>
          {Object.keys(items).map((key) => (
            <SelectOption key={key} value={key}>
              {items[key]}
            </SelectOption>
          ))}
        </SelectList>
      </Select>
    </div>
  );
};
